import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Pagination, InputBase, Tooltip, useTheme, useMediaQuery } from "@mui/material";
import { ToggleOnOutlined, ToggleOffOutlined } from "@mui/icons-material";
import Swal from "sweetalert2";
import { getWallets } from "api/financial";
import { toggleAccountStatus } from "api/wallet";
import { useSetBreadcrumb } from "hooks/BreadcrumbContext";

const FONT = "'Plus Jakarta Sans', 'Noto Sans Lao', system-ui, sans-serif";
const MONO = "'Space Grotesk', 'Noto Sans Lao', ui-monospace, monospace";
const CUR_SYM = { THB: "฿", LAK: "₭", USD: "$" };
const CURRENCIES = ["THB", "LAK", "USD"];
const SIGNS = ["all", "positive", "negative"];
const STATUSES = ["active", "inactive", "all"];
const AVATARS = ["#6d28d9", "#7c3aed", "#5b21b6", "#4338ca", "#3730a3", "#8b5cf6", "#7e22ce"];
const fmt = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const initials = (name) => {
  const p = (name || "?").trim().split(/\s+/);
  return (p.length >= 2 ? p[0][0] + p[1][0] : (name || "?").slice(0, 2)).toUpperCase();
};
const labelSx = { fontSize: "10.5px", letterSpacing: ".14em", color: "#a8a3b4", fontWeight: 700 };
const kpiVal = { fontFamily: MONO, fontSize: 24, fontWeight: 700, color: "#161420", mt: "10px" };
const headCell = { fontSize: 11, fontWeight: 700, letterSpacing: ".1em", color: "#a8a3b4", textTransform: "uppercase" };
const GRID = "2.2fr 1.4fr 1.3fr 1.3fr 0.9fr";

export default function FinancialWallets() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const currency = sp.get("currency") || "THB";
  const sign = sp.get("sign") || "all";
  const status = sp.get("status") || "active";

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ totalBalance: 0, totalCustomers: 0, withBalance: 0 });
  const [meta, setMeta] = useState({ pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const setParam = (k, v) => { const n = new URLSearchParams(sp); n.set(k, v); setSp(n); setPage(1); };

  const load = useCallback(async () => {
    const res = await getWallets({ currency, sign, status, search, page, limit: 20 });
    setRows(res && res.success ? res.data : []);
    setSummary((res && res.summary) || { totalBalance: 0, totalCustomers: 0, withBalance: 0 });
    setMeta((res && res.pagination) || { pages: 1, total: 0 });
  }, [currency, sign, status, search, page]);
  useEffect(() => { load(); }, [load]);

  const onToggle = async (e, r) => {
    e.stopPropagation();
    const activating = !r.validflag;
    const hasBalance = (r.THB || 0) !== 0 || (r.LAK || 0) !== 0 || (r.USD || 0) !== 0;
    if (!activating && hasBalance) {
      const confirm = await Swal.fire({
        icon: "warning",
        title: t("financial.deactivateFrozenTitle"),
        text: t("financial.deactivateFrozenWarning"),
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: t("financial.deactivateConfirm"),
        cancelButtonText: t("common.cancel"),
      });
      if (!confirm.isConfirmed) return;
    }
    const res = await toggleAccountStatus(r.id).catch((err) => err);
    if (res && res.success) {
      Swal.fire({ icon: "success", text: t(activating ? "financial.accountActivated" : "financial.accountDeactivated"), toast: true, position: "top-right", timer: 2000, showConfirmButton: false });
      load();
    } else {
      Swal.fire({ icon: "error", text: (res && res.message) || t("common.error"), toast: true, position: "top-right", timer: 2500, showConfirmButton: false });
    }
  };

  useSetBreadcrumb([{ label: t("financial.title"), to: "/financial" }, { label: t("financial.allWallets") }]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const grid = isMobile ? "1.6fr 1fr 1fr 0.7fr" : GRID; // mobile drops the WhatsApp column
  const maxBal = Math.max(...rows.map((r) => Math.abs(r[currency] || 0)), 1);
  const sym = CUR_SYM[currency];

  return (
    <Box sx={{ fontFamily: FONT, maxWidth: 1280, mr: "auto", p: { xs: "20px", md: "28px 40px 60px" } }}>
      {/* title + currency tabs */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "14px" }}>
        <Box>
          <Box component="h1" sx={{ m: 0, fontSize: 26, fontWeight: 800, color: "#16131f", letterSpacing: "-.01em" }}>{t("financial.allWallets")}</Box>
          <Box component="p" sx={{ m: "4px 0 0", fontSize: 13, color: "#9b96a6" }}>{t("financial.walletsSubtitle")}</Box>
        </Box>
        <Box sx={{ display: "flex", gap: "4px", background: "#ece8f6", p: "4px", borderRadius: "12px" }}>
          {CURRENCIES.map((c) => {
            const active = currency === c;
            return (
              <Box component="button" key={c} onClick={() => setParam("currency", c)}
                sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, padding: "8px 20px", borderRadius: "9px", border: "none", cursor: "pointer", background: active ? "#6d28d9" : "transparent", color: active ? "#fff" : "#7c7787" }}>{c}</Box>
            );
          })}
        </Box>
      </Box>

      {/* KPI strip */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4,1fr)" }, gap: "16px", mt: "22px" }}>
        <Box sx={{ background: "linear-gradient(155deg,#fff 0%,#f5efff 100%)", border: "1px solid #ece4fb", borderRadius: "18px", p: "18px 20px", boxShadow: "0 1px 2px rgba(20,18,30,.04),0 14px 28px -20px rgba(86,40,160,.22)" }}>
          <Box sx={labelSx}>{t("financial.currency").toUpperCase()}</Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mt: "10px" }}>
            <Box sx={{ width: 36, height: 36, borderRadius: "11px", background: "#f3f0fc", border: "1px solid #e2d9fa", color: "#5b21b6", fontFamily: MONO, fontWeight: 700, fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center" }}>{sym}</Box>
            <Box sx={{ fontFamily: MONO, fontSize: 24, fontWeight: 700, color: "#161420" }}>{currency}</Box>
          </Box>
        </Box>
        <Kpi label={t("financial.totalBalance")} value={`${sym}${fmt(summary.totalBalance)}`} hint={t("financial.acrossAllCustomers")} />
        <Kpi label={t("financial.totalCustomers")} value={fmt(summary.totalCustomers)} hint={t("financial.registeredWallets")} />
        <Kpi label={t("financial.withBalance")} value={fmt(summary.withBalance)} hint={t("financial.haveNonZero")} valueColor="#6d28d9" />
      </Box>

      {/* filter bar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mt: "22px", flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", gap: "4px", background: "#fff", border: "1px solid #ece9f1", p: "4px", borderRadius: "11px", boxShadow: "0 1px 2px rgba(20,18,30,.04)" }}>
          {SIGNS.map((s) => {
            const active = sign === s;
            return (
              <Box component="button" key={s} onClick={() => setParam("sign", s)}
                sx={{ fontFamily: FONT, fontSize: "12.5px", fontWeight: 700, padding: "7px 15px", borderRadius: "8px", border: "none", cursor: "pointer", background: active ? "#6d28d9" : "transparent", color: active ? "#fff" : "#7c7787" }}>{t(`financial.${s}`)}</Box>
            );
          })}
        </Box>
        <Box sx={{ display: "flex", gap: "4px", background: "#fff", border: "1px solid #ece9f1", p: "4px", borderRadius: "11px", boxShadow: "0 1px 2px rgba(20,18,30,.04)" }}>
          {STATUSES.map((s) => {
            const active = status === s;
            return (
              <Box component="button" key={s} onClick={() => setParam("status", s)}
                sx={{ fontFamily: FONT, fontSize: "12.5px", fontWeight: 700, padding: "7px 15px", borderRadius: "8px", border: "none", cursor: "pointer", background: active ? "#6d28d9" : "transparent", color: active ? "#fff" : "#7c7787" }}>{t(`financial.walletStatus.${s}`)}</Box>
            );
          })}
        </Box>
        <Box sx={{ flex: 1, minWidth: 200, maxWidth: 340 }}>
          <InputBase fullWidth placeholder={`${t("financial.customer")}…`} value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 500, px: "14px", py: "9px", borderRadius: "11px", border: "1px solid #ece9f1", background: "#fff", color: "#2c2937", boxShadow: "0 1px 2px rgba(20,18,30,.04)" }} />
        </Box>
        <Box sx={{ ml: "auto", fontSize: "12.5px", color: "#a8a3b4", fontWeight: 600 }}>{meta.total || 0} {t("financial.results")}</Box>
      </Box>

      {/* table */}
      <Box sx={{ background: "#fff", border: "1px solid #ece9f1", borderRadius: "22px", overflow: "hidden", mt: "16px", boxShadow: "0 1px 2px rgba(20,18,30,.04),0 16px 40px -28px rgba(20,18,30,.18)" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: grid, alignItems: "center", p: "13px 24px", background: "#faf9fc", borderBottom: "1.5px solid #ece9f1" }}>
          <Box sx={headCell}>{t("financial.customer")}</Box>
          {!isMobile && <Box sx={headCell}>{t("financial.whatsapp")}</Box>}
          <Box sx={{ ...headCell, textAlign: "right" }}>{currency} {t("financial.balance")}</Box>
          <Box sx={{ ...headCell, color: "#6d28d9", textAlign: "right" }}>{t("financial.available")}</Box>
          <Box sx={{ ...headCell, textAlign: "center" }}>{t("financial.walletStatus.label")}</Box>
        </Box>
        {rows.length === 0 ? (
          <Box sx={{ p: "60px 24px", textAlign: "center", color: "#b9b4c4" }}>
            <Box sx={{ fontSize: 32, mb: "10px" }}>🔍</Box>
            <Box sx={{ fontSize: 14, fontWeight: 600, color: "#6b6775" }}>{t("financial.noData")}</Box>
          </Box>
        ) : rows.map((r, i) => {
          const bal = r[currency] || 0;
          const avail = r.available ? r.available[currency] : bal;
          const col = bal > 0 ? "#161420" : bal < 0 ? "#c0343a" : "#b9b4c4";
          const inactive = r.validflag === false;
          return (
            <Box key={r.accountId} onClick={() => navigate(`/financial/wallets/${r.accountId}/transactions`)}
              sx={{ display: "grid", gridTemplateColumns: grid, alignItems: "center", p: "0 24px", borderTop: "1px solid #f4f2f8", minHeight: 60, cursor: "pointer", opacity: inactive ? 0.55 : 1, "&:hover": { background: "#faf9fc" } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px", py: "14px" }}>
                <Box sx={{ width: 36, height: 36, flex: "none", borderRadius: "11px", background: AVATARS[i % AVATARS.length], color: "#fff", fontFamily: MONO, fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>{initials(r.customer?.name)}</Box>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ fontSize: 14, fontWeight: 700, color: "#1c1a26", lineHeight: 1.2 }}>{r.customer?.name || "—"}</Box>
                  {/* On mobile the WhatsApp column is hidden — show the number under the name instead. */}
                  {isMobile && r.customer?.whatsapp && <Box sx={{ fontSize: 11.5, color: "#9b96a6", fontFamily: MONO, mt: "2px" }}>{r.customer.whatsapp}</Box>}
                  {bal !== 0 && <Box sx={{ fontSize: "10.5px", fontWeight: 700, color: "#6d28d9", background: "#f3f0fc", padding: "2px 7px", borderRadius: "999px", mt: "3px", display: "inline-block" }}>{t("financial.active")}</Box>}
                </Box>
              </Box>
              {!isMobile && <Box sx={{ fontSize: 13, color: "#6b6775", fontFamily: MONO }}>{r.customer?.whatsapp || "—"}</Box>}
              <Box sx={{ textAlign: "right", pr: "18px" }}>
                <Box sx={{ fontFamily: MONO, fontSize: 15, fontWeight: 600, color: col }}>{sym}{fmt(bal)}</Box>
                <Box sx={{ height: 5, borderRadius: "3px", background: "#f0eef5", mt: "6px", overflow: "hidden", maxWidth: 100, ml: "auto" }}>
                  <Box sx={{ height: "100%", borderRadius: "3px", background: "linear-gradient(90deg,#8b5cf6,#6d28d9)", width: `${Math.min((Math.abs(bal) / maxBal) * 100, 100)}%` }} />
                </Box>
              </Box>
              <Box sx={{ textAlign: "right", fontFamily: MONO, fontSize: 15, fontWeight: 700, color: col }}>{sym}{fmt(avail)}</Box>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Tooltip title={t(inactive ? "financial.walletStatus.activate" : "financial.walletStatus.deactivate")}>
                  <Box component="span" onClick={(e) => onToggle(e, r)}
                    sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: inactive ? "#b9b4c4" : "#15a36a", "&:hover": { opacity: 0.75 } }}>
                    {inactive ? <ToggleOffOutlined sx={{ fontSize: 30 }} /> : <ToggleOnOutlined sx={{ fontSize: 30 }} />}
                  </Box>
                </Tooltip>
              </Box>
            </Box>
          );
        })}
      </Box>

      {meta.pages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: "20px" }}>
          <Pagination color="secondary" count={meta.pages} page={page} onChange={(e, v) => setPage(v)} />
        </Box>
      )}
    </Box>
  );
}

function Kpi({ label, value, hint, valueColor }) {
  return (
    <Box sx={{ background: "#fff", border: "1px solid #ece9f1", borderRadius: "18px", p: "18px 20px", boxShadow: "0 1px 2px rgba(20,18,30,.04),0 14px 28px -20px rgba(20,18,30,.12)" }}>
      <Box sx={labelSx}>{label.toUpperCase()}</Box>
      <Box sx={{ ...kpiVal, color: valueColor || "#161420" }}>{value}</Box>
      <Box sx={{ fontSize: "11.5px", color: "#9b96a6", mt: "4px" }}>{hint}</Box>
    </Box>
  );
}
